/**
 * Duplicate detection utilities for products
 */

const IGNORED_KEY = "frugalstack_ignored_duplicates";

/**
 * Normalize name for comparison
 */
function normalizeName(name) {
  return name
    .toLowerCase()
    .replace(/[^\w\s]/g, "") // Remove punctuation
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim();
}

/**
 * Calculate Levenshtein distance (optimized with early exit)
 */
function levenshteinDistance(a, b, maxDistance = 10) {
  if (Math.abs(a.length - b.length) > maxDistance) return maxDistance + 1;

  const matrix = [];
  for (let i = 0; i <= a.length; i++) {
    matrix[i] = [i];
    for (let j = 1; j <= b.length; j++) {
      if (i === 0) {
        matrix[i][j] = j;
      } else {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1, // deletion
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j - 1] + cost, // substitution
        );
      }
    }
  }
  return matrix[a.length][b.length];
}

/**
 * Calculate similarity score (0-100)
 */
function similarityScore(a, b) {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 100;
  const distance = levenshteinDistance(a, b);
  return Math.round((1 - distance / maxLen) * 100);
}

/**
 * Token-based similarity (for long product names)
 */
function tokenSimilarity(a, b) {
  const tokensA = new Set(a.split(" ").filter((t) => t.length > 2));
  const tokensB = new Set(b.split(" ").filter((t) => t.length > 2));
  const intersection = [...tokensA].filter((t) => tokensB.has(t));
  const union = new Set([...tokensA, ...tokensB]);
  return union.size > 0 ? (intersection.length / union.size) * 100 : 0;
}

/**
 * Check if two products are duplicates
 */
function checkDuplicate(a, b) {
  // 1. SKU/ASIN match (highest priority)
  if (a.sku && b.sku && a.sku === b.sku) {
    return { type: "sku", reason: `Same SKU: ${a.sku}` };
  }

  // 2. Exact name match
  const nameA = normalizeName(a.name || "");
  const nameB = normalizeName(b.name || "");

  if (nameA && nameB && nameA === nameB) {
    return { type: "exact", reason: "Exact name match" };
  }

  // 3. Fuzzy name match (only for reasonably similar lengths)
  if (!nameA || !nameB) return null;

  const lenRatio =
    Math.min(nameA.length, nameB.length) / Math.max(nameA.length, nameB.length);
  if (lenRatio < 0.7) return null; // Too different in length

  // For short names, use Levenshtein
  if (nameA.length < 50) {
    const score = similarityScore(nameA, nameB);
    if (score >= 85) {
      return { type: "fuzzy", reason: `${score}% name similarity` };
    }
  } else {
    // For long names, use token similarity
    const tokenScore = tokenSimilarity(nameA, nameB);
    if (tokenScore >= 80) {
      return { type: "fuzzy", reason: `${Math.round(tokenScore)}% token match` };
    }
  }

  return null;
}

/**
 * Detect duplicate groups from products array
 * @param {Array} products - Array of product objects
 * @param {Set} ignoredPairs - Set of ignored pair keys ("id1:id2")
 * @returns {Array} Array of duplicate groups: [{ products: [...], matchType, matchReasons }]
 */
export function findDuplicateGroups(products, ignoredPairs = new Set()) {
  const groups = [];
  const processed = new Set();

  for (let i = 0; i < products.length; i++) {
    if (processed.has(products[i].id)) continue;

    const group = {
      products: [products[i]],
      matchType: null,
      matchReasons: [],
    };

    for (let j = i + 1; j < products.length; j++) {
      if (processed.has(products[j].id)) continue;

      const pairKey = [products[i].id, products[j].id].sort().join(":");
      if (ignoredPairs.has(pairKey)) continue;

      const match = checkDuplicate(products[i], products[j]);
      if (match) {
        group.products.push(products[j]);
        group.matchType = group.matchType || match.type;
        group.matchReasons.push(match.reason);
        processed.add(products[j].id);
      }
    }

    if (group.products.length > 1) {
      processed.add(products[i].id);
      groups.push(group);
    }
  }

  // Sort groups by match type priority (sku > exact > fuzzy)
  const typePriority = { sku: 0, exact: 1, fuzzy: 2 };
  groups.sort(
    (a, b) => (typePriority[a.matchType] || 3) - (typePriority[b.matchType] || 3),
  );

  return groups;
}

/**
 * Get ignored pairs from localStorage
 */
export function getIgnoredPairs() {
  try {
    const stored = localStorage.getItem(IGNORED_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Add an ignored pair to localStorage
 */
export function addIgnoredPair(id1, id2) {
  const pairs = getIgnoredPairs();
  const key = [id1, id2].sort().join(":");
  pairs.add(key);
  localStorage.setItem(IGNORED_KEY, JSON.stringify([...pairs]));
}

/**
 * Clear all ignored pairs
 */
export function clearIgnoredPairs() {
  localStorage.removeItem(IGNORED_KEY);
}
